import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Dialog, 
  DialogTitle,
  DialogContent, 
  DialogActions,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  TextField,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import { getFacilityById } from '../../services/facilityService';
import { format } from 'date-fns';

// Styled Components - Modern Theme (matching other pages)
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '& .MuiTypography-root': {
    color: 'white',
  },
}));

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 3),
}));

const PrintContainer = styled(Paper)(({ theme }) => ({
  width: '8.5in',
  minHeight: '11in',
  maxWidth: '100%',
  margin: '0 auto',
  padding: '0.5in',
  backgroundColor: 'white',
  fontSize: '12pt',
  lineHeight: '1.4',
  fontFamily: 'Times, "Times New Roman", serif',
  '@media print': {
    margin: 0,
    padding: '0.5in',
    boxShadow: 'none',
    border: 'none',
    width: '100%',
    minHeight: 'auto',
  }
}));

const RxHeader = styled(Box)({
  borderBottom: '2px solid #000',
  paddingBottom: '16px',
  marginBottom: '24px',
});

const FacilitySection = styled(Box)({
  fontSize: '14pt',
  fontWeight: 'bold',
});

const ProviderSection = styled(Box)({
  fontSize: '12pt',
  textAlign: 'right',
});

const PatientSection = styled(Box)({
  fontSize: '12pt',
  marginBottom: '24px',
  padding: '12px',
  border: '1px solid #ccc',
  borderRadius: '4px',
});

const MedicationBox = styled(Box)({
  marginBottom: '20px',
  padding: '16px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  backgroundColor: '#fafafa',
});

const SignatureSection = styled(Box)({
  marginTop: '40px',
  borderTop: '1px solid #ccc',
  paddingTop: '20px',
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
  '@media print': {
    display: 'none',
  }
}));

const NoPrintBox = styled(Box)({
  '@media print': {
    display: 'none',
  }
});

const PrescriptionPrintComponent = ({ 
  prescriptions = [], 
  patient = null, 
  encounter = null,
  facilityId = null,
  providerInfo = null,
  open = false,
  onClose = () => {},
  onPrint = () => {}
}) => {
  const [facilityData, setFacilityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signaturePhysicianName, setSignaturePhysicianName] = useState('');

  // Get active prescriptions only
  const activePrescriptions = useMemo(() => 
    prescriptions.filter(p => p.status === 'active' || !p.status),
    [prescriptions]
  );

  // Check if a prescription has a provider name available
  const hasProviderName = useCallback((prescription) => {
    return prescription?.provider || 
           prescription?.prescribingProvider || 
           prescription?.providerName ||
           prescription?.prescriber ||
           providerInfo?.name;
  }, [providerInfo?.name]);

  // Reset signature name when dialog opens with new prescription
  useEffect(() => {
    if (open) {
      // Check if the prescription has a provider name, if so don't reset
      const prescription = activePrescriptions[0];
      if (!hasProviderName(prescription)) {
        setSignaturePhysicianName('');
      }
    }
  }, [open, activePrescriptions, hasProviderName]);

  useEffect(() => {
    const fetchFacilityData = async () => {
      if (!facilityId || !open) return;
      
      try {
        setLoading(true);
        setError('');
        const facility = await getFacilityById(facilityId);
        setFacilityData(facility);
      } catch (err) {
        console.error('Error fetching facility data:', err);
        setError('Unable to load facility information');
      } finally {
        setLoading(false);
      }
    };

    fetchFacilityData();
  }, [facilityId, open]);

  const handlePrint = () => {
    window.print();
    onPrint();
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      return format(new Date(date), 'MM/dd/yyyy');
    } catch {
      return '';
    }
  };

  const getProviderName = (prescription) => {
    return prescription?.provider || 
           prescription?.prescribingProvider || 
           prescription?.providerName ||
           prescription?.prescriber ||
           providerInfo?.name || 
           signaturePhysicianName ||
           '';
  };

  // Get the display name for the signature line
  const getSignatureDisplayName = (prescription) => {
    const existingName = prescription?.provider || 
                         prescription?.prescribingProvider || 
                         prescription?.providerName ||
                         prescription?.prescriber ||
                         providerInfo?.name;
    return existingName || signaturePhysicianName || '';
  };

  const getProviderNPI = (prescription) => {
    return prescription.providerNPI || 
           prescription.npi || 
           providerInfo?.npi || 
           '';
  };

  const getProviderDEA = (prescription) => {
    return prescription.providerDEA || 
           prescription.dea || 
           providerInfo?.dea || 
           '';
  };

  if (!open) return null;

  return (
    <StyledDialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LocalPharmacyIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
              Prescription Print Preview
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>
              Review and print prescription document
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={onClose} 
          sx={{ 
            color: 'white',
            '&:hover': { 
              backgroundColor: 'rgba(255, 255, 255, 0.1)' 
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: '#f5f5f5' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {activePrescriptions.length === 0 && !loading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No active prescriptions available to print.
          </Alert>
        )}

        {activePrescriptions.length > 0 && !loading && (
          <PrintContainer elevation={3}>
            {/* Header Section */}
            <RxHeader>
              <Grid container spacing={2}>
                {/* Facility Information - Left */}
                <Grid item xs={6}>
                  <FacilitySection>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {facilityData?.name || 'Healthcare Facility'}
                    </Typography>
                    {facilityData?.location && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {facilityData.location}
                      </Typography>
                    )}
                    {facilityData?.phone && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        Phone: {facilityData.phone}
                      </Typography>
                    )}
                    {facilityData?.fax && (
                      <Typography variant="body2">
                        Fax: {facilityData.fax}
                      </Typography>
                    )}
                  </FacilitySection>
                </Grid>

                {/* Provider Information - Right */}
                <Grid item xs={6}>
                  <ProviderSection>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {getProviderName(activePrescriptions[0])}
                    </Typography>
                    {getProviderNPI(activePrescriptions[0]) && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        NPI: {getProviderNPI(activePrescriptions[0])}
                      </Typography>
                    )}
                    {getProviderDEA(activePrescriptions[0]) && (
                      <Typography variant="body2">
                        DEA: {getProviderDEA(activePrescriptions[0])}
                      </Typography>
                    )}
                  </ProviderSection>
                </Grid>
              </Grid>
            </RxHeader>

            {/* Patient Information */}
            <PatientSection>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Patient Information
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {patient?.name || 'Patient Name Not Available'}
                  </Typography>
                  <Typography variant="body2">
                    DOB: {patient?.dateOfBirth ? formatDate(patient.dateOfBirth) : 'Not Available'}
                  </Typography>
                  {patient?.mrn && (
                    <Typography variant="body2">
                      MRN: {patient.mrn}
                    </Typography>
                  )}
                  {encounter?.finNumber && (
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      FIN: {encounter.finNumber}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>
                    Date: {formatDate(new Date())}
                  </Typography>
                </Grid>
              </Grid>
            </PatientSection>

            {/* Prescriptions */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '1px solid #ccc', pb: 1 }}>
              PRESCRIPTIONS
            </Typography>

            {activePrescriptions.map((prescription, index) => (
              <Box key={prescription.id || index} sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Prescription #{index + 1} - {prescription.createdAt ? formatDate(prescription.createdAt) : formatDate(new Date())}
                </Typography>
                
                {prescription.medications && prescription.medications.map((med, medIndex) => (
                  <MedicationBox key={medIndex}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '14pt', mb: 1 }}>
                      Rx: {med.name}
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Strength/Dosage:</strong> {med.dosage || 'As directed'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Route:</strong> {med.route || 'Not specified'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Frequency:</strong> {med.frequency || 'As directed'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Duration:</strong> {med.duration || 'Until finished'}
                        </Typography>
                      </Grid>
                    </Grid>

                    {med.instructions && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'white', border: '1px solid #ddd' }}>
                        <Typography variant="body2">
                          <strong>Sig:</strong> {med.instructions}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 4 }}>
                      <Typography variant="body2">
                        Quantity: ____________
                      </Typography>
                      <Typography variant="body2">
                        Refills: ____________
                      </Typography>
                      <Typography variant="body2">
                        Generic Substitution: ☐ Yes ☐ No
                      </Typography>
                    </Box>
                  </MedicationBox>
                ))}

                {prescription.pharmacy && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f0f0', border: '1px solid #ddd' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Pharmacy: {prescription.pharmacy.name}
                    </Typography>
                    {prescription.pharmacy.address && (
                      <Typography variant="body2">
                        {prescription.pharmacy.address}
                      </Typography>
                    )}
                    {prescription.pharmacy.phone && (
                      <Typography variant="body2">
                        Phone: {prescription.pharmacy.phone}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            ))}

            {/* Signature Section */}
            <SignatureSection>
              {/* Editable physician name input - hidden on print */}
              {!hasProviderName(activePrescriptions[0]) && (
                <NoPrintBox sx={{ mb: 3, p: 2, bgcolor: '#fff8e1', border: '1px solid #ffcc02', borderRadius: '8px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <EditIcon sx={{ color: '#f57c00', fontSize: '20px' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                      Physician name not found. Please enter the attending physician's name for the signature line:
                    </Typography>
                  </Box>
                  <StyledTextField
                    fullWidth
                    size="small"
                    placeholder="Enter physician name (e.g., Dr. John Smith, MD)"
                    value={signaturePhysicianName}
                    onChange={(e) => setSignaturePhysicianName(e.target.value)}
                    sx={{ 
                      mt: 1,
                      bgcolor: 'white',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      }
                    }}
                  />
                </NoPrintBox>
              )}

              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Prescriber Signature:
                  </Typography>
                  <Box sx={{ borderBottom: '1px solid #000', height: '40px', mb: 2 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {getSignatureDisplayName(activePrescriptions[0]) || '________________________________'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Date:
                  </Typography>
                  <Box sx={{ borderBottom: '1px solid #000', height: '40px', mb: 2 }} />
                  <Typography variant="body2">
                    {formatDate(new Date())}
                  </Typography>
                </Grid>
              </Grid>
            </SignatureSection>

            {/* Footer */}
            <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #ccc', textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                This prescription was generated electronically and is valid without signature if electronically transmitted.
              </Typography>
            </Box>
          </PrintContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
        <ModernButton 
          variant="outlined" 
          onClick={onClose}
          sx={{ 
            borderColor: 'grey.400',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'grey.500',
              bgcolor: 'grey.100'
            }
          }}
        >
          Close
        </ModernButton>
        <ModernButton 
          variant="contained" 
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={loading || activePrescriptions.length === 0}
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
            },
            '&:disabled': {
              background: 'grey.300'
            }
          }}
        >
          Print Prescription
        </ModernButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default PrescriptionPrintComponent; 